// IncomingCreateWebhookModal.jsx

import React from 'react';

// Utilities
import { cleanString } from '../../../apiUtils';
import { normalizeIncomingAction } from '../../utils/integrationUtils';

// Constants
import { INCOMING_ACTION_OPTIONS } from '../../constants/integrationOptions';

// Components
import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';
import SelectInput from 'components/selectinput/SelectInput';
import TextInput from 'components/textinput/TextInput';
import ModalButtons from 'components/modal/buttons/ModalButtons';

/**
 * IncomingCreateWebhookModal
 *
 * Renders the modal used to create a new incoming webhook integration.
 *
 * @param {object} theme - Active theme palette values.
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {function} onClose - Handler to close the modal.
 * @param {boolean} canManageCredentials - Whether the user can create credentials.
 * @param {string} busyActionKey - Current busy action key used to disable actions.
 * @param {string} incomingDraftName - Draft webhook name.
 * @param {string} incomingDraftAction - Draft webhook action type.
 * @param {string} incomingDraftTypeId - Draft target type id.
 * @param {array} typeRows - Available type options.
 * @param {function} onDraftNameChange - Handler to update draft name.
 * @param {function} onDraftActionChange - Handler to update draft action.
 * @param {function} onDraftTypeIdChange - Handler to update draft type id.
 * @param {function} onCreate - Handler to create webhook.
 *
 * @returns {ReactNode} The rendered create incoming webhook modal.
 */
const IncomingCreateWebhookModal = ({
    theme,
    isOpen,
    onClose,
    canManageCredentials,
    busyActionKey,
    incomingDraftName,
    incomingDraftAction,
    incomingDraftTypeId,
    typeRows,
    onDraftNameChange,
    onDraftActionChange,
    onDraftTypeIdChange,
    onCreate,
}) => {

    // ----- RENDER ----- //

    return (
        <Modal
            title="Add Webhook"
            isOpen={isOpen}
            onClose={onClose}
            width="620px"
            maxHeight="85vh"
        >
            <div className="api-integrations-modal-body">

                {/* INTRO */}
                <div
                    className="api-integrations-subtitle"
                    style={{
                        color: theme.foregroundMuted,
                    }}
                >
                    Create an inbound endpoint for Zapier or other tools to send data into Orbital.
                </div>

                {/* FORM */}
                <div className="api-integrations-form-grid">
                    <div
                        className="api-integrations-field-label"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Webhook name
                    </div>
                    <TextInput
                        value={incomingDraftName}
                        onChange={(event) => onDraftNameChange(event.target.value)}
                        placeholder="Example: Google Sheets -> Tasks"
                        disabled={!canManageCredentials}
                    />

                    <div
                        className="api-integrations-field-label"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Action
                    </div>
                    <SelectInput
                        value={incomingDraftAction}
                        onChange={(event) => onDraftActionChange(normalizeIncomingAction(event.target.value))}
                        disabled={!canManageCredentials}
                    >
                        {INCOMING_ACTION_OPTIONS.map((row) => (
                            <option key={row.value} value={row.value}>{row.label}</option>
                        ))}
                    </SelectInput>

                    <div
                        className="api-integrations-field-label"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Target type
                    </div>
                    <SelectInput
                        value={incomingDraftTypeId}
                        onChange={(event) => onDraftTypeIdChange(cleanString(event.target.value))}
                        disabled={!canManageCredentials || typeRows.length === 0}
                    >
                        {typeRows.map((row) => (
                            <option key={row.typeId} value={row.typeId}>{row.label}</option>
                        ))}
                    </SelectInput>
                </div>

                {/* ACTIONS */}
                <ModalButtons>
                    <Button
                        label="Cancel"
                        onClick={onClose}
                        enabled={busyActionKey !== 'create-incoming-hook'}
                        height="30px"
                        fontSize="12px"
                        borderRadius="6px"
                    />
                    <Button
                        label="Create Webhook"
                        onClick={onCreate}
                        enabled={canManageCredentials && busyActionKey !== 'create-incoming-hook'}
                        height="30px"
                        fontSize="12px"
                        borderRadius="6px"
                    />
                </ModalButtons>
            </div>
        </Modal>
    );
};

// Dev Tools
IncomingCreateWebhookModal.displayName = 'IncomingCreateWebhookModal';

export default IncomingCreateWebhookModal;
